<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  authorizeWooCommerce,
  saveWooCommercePreferences,
} from '../../../api/woocommerce';
import {
  buildWooCommerceConversationPanelMode,
  WOO_COMMERCE_CONVERSATION_PANEL_MODES,
} from '../../../helpers/stores';

defineProps({
  error: { type: String, default: '' },
});

const store = useStore();
const { t } = useI18n();
const integrationLoaded = ref(false);
const isSubmitting = ref(false);
const isSavingPreferences = ref(false);
const isAutomationAccordionOpen = ref(false);
const viewMode = ref('overview');
const editingHookId = ref(null);
const storeName = ref('');
const storeUrl = ref('');
const consumerKey = ref('');
const consumerSecret = ref('');
const requestStoreUrl = ref('');
const disableSslVerification = ref(false);
const conversationPanelMode = ref(WOO_COMMERCE_CONVERSATION_PANEL_MODES.INLINE);
const abandonedCartsEndpoint = ref('');
const formError = ref('');
const diagnosticsRefreshHookId = ref(null);
let diagnosticsPollTimeout = null;

const integration = useFunctionGetter(
  'integrations/getIntegration',
  'woocommerce'
);
const integrationId = computed(() => integration.value?.id || 'woocommerce');
const integrationName = computed(
  () => integration.value?.name || 'WooCommerce'
);
const connectedHooks = computed(() => {
  return (integration.value?.hooks || []).map(hook => ({
    id: hook.id,
    storeName: hook.settings?.store_name || hook.store_name || '',
    storeUrl: hook.settings?.store_url || hook.reference_id || '',
    consumerKey:
      hook.settings?.consumer_key ||
      hook.consumer_key ||
      hook.access_token ||
      '',
    consumerSecret:
      hook.settings?.consumer_secret || hook.consumer_secret || '',
    requestStoreUrl: hook.settings?.request_store_url || '',
    disableSslVerification: Boolean(hook.settings?.disable_ssl_verification),
    conversationPanelMode: buildWooCommerceConversationPanelMode(hook),
    abandonedCartsEndpoint: hook.settings?.abandoned_carts_endpoint || '',
    webhookToken: hook.settings?.webhook_token || '',
    diagnostics: hook.settings?.diagnostics || {},
    diagnosticsLog: hook.settings?.diagnostics_log || [],
    settings: hook.settings || {},
  }));
});
const integrationDescription = computed(
  () =>
    integration.value?.description ||
    t('INTEGRATION_SETTINGS.WOOCOMMERCE.DESCRIPTION')
);
const currentConversationPanelMode = computed(
  () =>
    integration.value?.conversation_panel_mode ||
    buildWooCommerceConversationPanelMode(connectedHooks.value[0])
);
const selectedHook = computed(() => {
  return (
    connectedHooks.value.find(hook => hook.id === editingHookId.value) || null
  );
});
const accountId = computed(() => store.getters.getCurrentAccountId);
const pluginConfig = computed(
  () => window.chatwoot?.plugins?.woocommerce?.config || {}
);
const automationAuthHeader = computed(
  () => pluginConfig.value.AUTOMATION_AUTH_HEADER
);
const tokenFallbackHeader = computed(
  () => pluginConfig.value.WEBHOOK_TOKEN_HEADER
);
const automationEventKeys = computed(
  () => pluginConfig.value.AUTOMATION_EVENT_KEYS || []
);
const automationEventLabels = computed(
  () => pluginConfig.value.AUTOMATION_EVENT_LABELS || {}
);
const payloadFields = computed(
  () => pluginConfig.value.AUTOMATION_PAYLOAD_FIELDS || []
);
const abandonedCartExample = computed(() =>
  JSON.stringify(
    [
      {
        id: 'cart_123',
        checkout_url: 'https://store.example.com/checkout?recover=cart_123',
        customer: {
          email: 'jane.doe@example.com',
          phone: '+55 11 99999-0000',
          first_name: 'Jane',
          last_name: 'Doe',
        },
        total: '189.90',
        currency: 'BRL',
        updated_at: '2026-04-30T18:40:00Z',
        items: [
          {
            product_id: 321,
            name: 'Hermes Starter Plan',
            quantity: 1,
            total: '189.90',
          },
        ],
      },
    ],
    null,
    2
  )
);
const abandonedCartFallbackExample = computed(() =>
  JSON.stringify(
    [
      {
        id: 'cart_987',
        recover_url: 'https://store.example.com/cart/recover/cart_987',
        billing: {
          email: 'john.smith@example.com',
          phone: '+55 21 98888-7777',
          first_name: 'John',
          last_name: 'Smith',
        },
        cart_total: '79.00',
        cart_currency: 'BRL',
      },
    ],
    null,
    2
  )
);
const automationWebhookUrl = computed(
  () =>
    `${window.location.origin}/woocommerce/accounts/${accountId.value}/events`
);
const submitLabel = computed(() =>
  editingHookId.value
    ? t('INTEGRATION_SETTINGS.WOOCOMMERCE.UPDATE_CONNECTION')
    : t('INTEGRATION_SETTINGS.WOOCOMMERCE.SAVE_CONNECTION')
);
const savePreferencesLabel = computed(() =>
  t('INTEGRATION_SETTINGS.WOOCOMMERCE.SAVE_GENERAL_SETTINGS')
);
const conversationModeOptions = computed(() => [
  {
    value: WOO_COMMERCE_CONVERSATION_PANEL_MODES.INLINE,
    label: t(
      'INTEGRATION_SETTINGS.WOOCOMMERCE.CONVERSATION_PANEL_MODES.INLINE'
    ),
    hint: t(
      'INTEGRATION_SETTINGS.WOOCOMMERCE.CONVERSATION_PANEL_MODE_HINTS.INLINE'
    ),
  },
  {
    value: WOO_COMMERCE_CONVERSATION_PANEL_MODES.SIDEBAR,
    label: t(
      'INTEGRATION_SETTINGS.WOOCOMMERCE.CONVERSATION_PANEL_MODES.SIDEBAR'
    ),
    hint: t(
      'INTEGRATION_SETTINGS.WOOCOMMERCE.CONVERSATION_PANEL_MODE_HINTS.SIDEBAR'
    ),
  },
]);

const statusClassMap = {
  ok: 'bg-n-teal-9 text-white',
  failed: 'bg-n-ruby-9 text-white',
  pending: 'bg-n-amber-9 text-white',
  not_configured: 'bg-n-slate-8 text-white',
};

const statusGlyphMap = {
  ok: 'OK',
  failed: 'X',
  pending: '...',
  not_configured: '-',
};

const buildAutomationHeaderValue = token => {
  return `${automationAuthHeader.value}: Bearer ${token || '...'}`;
};

const buildFallbackHeaderValue = token => {
  return `${tokenFallbackHeader.value}: ${token || '...'}`;
};

const formatTimestamp = value => {
  if (!value) {
    return '';
  }

  const parsedValue = new Date(value);
  if (Number.isNaN(parsedValue.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsedValue);
};

const normalizeStatus = status => {
  return ['ok', 'failed', 'pending', 'not_configured'].includes(status)
    ? status
    : 'pending';
};

const buildDiagnosticMeta = diagnostic => {
  const time = formatTimestamp(
    diagnostic?.last_received_at || diagnostic?.checked_at || ''
  );
  return time
    ? t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.LAST_CHECK', { time })
    : '';
};

const diagnosticStatusLabel = status => {
  switch (status) {
    case 'ok':
      return t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.STATUS.OK');
    case 'failed':
      return t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.STATUS.FAILED');
    case 'not_configured':
      return t(
        'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.STATUS.NOT_CONFIGURED'
      );
    default:
      return t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.STATUS.PENDING');
  }
};

const buildDiagnostics = hook => {
  const diagnostics = hook.diagnostics || {};
  return [
    {
      key: 'webhooks',
      label: t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.CHECKS.WEBHOOKS'),
      ...(diagnostics.webhooks || {
        status: 'pending',
        message: t(
          'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.PENDING_MESSAGE'
        ),
      }),
    },
    {
      key: 'orders',
      label: t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.CHECKS.ORDERS'),
      ...(diagnostics.orders || {
        status: 'pending',
        message: t(
          'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.PENDING_MESSAGE'
        ),
      }),
    },
    {
      key: 'abandoned_carts',
      label: t(
        'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.CHECKS.ABANDONED_CARTS'
      ),
      ...(diagnostics.abandoned_carts || {
        status: 'not_configured',
        message: t(
          'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.NOT_CONFIGURED_MESSAGE'
        ),
      }),
    },
    {
      key: 'events',
      label: t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.CHECKS.EVENTS'),
      ...(diagnostics.events || {
        status: 'pending',
        message: t(
          'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.PENDING_MESSAGE'
        ),
      }),
    },
  ].map(entry => ({
    ...entry,
    status: normalizeStatus(entry.status),
    meta: buildDiagnosticMeta(entry),
  }));
};

const hasPendingDiagnostics = hook => {
  return buildDiagnostics(hook).some(item => item.status === 'pending');
};

const stopDiagnosticsPolling = () => {
  if (diagnosticsPollTimeout) {
    window.clearTimeout(diagnosticsPollTimeout);
    diagnosticsPollTimeout = null;
  }
};

const scheduleDiagnosticsPolling = () => {
  stopDiagnosticsPolling();
  if (!connectedHooks.value.some(hasPendingDiagnostics)) {
    return;
  }

  diagnosticsPollTimeout = window.setTimeout(async () => {
    await store.dispatch('integrations/get');
    scheduleDiagnosticsPolling();
  }, 4000);
};

const refreshIntegrationState = async () => {
  await store.dispatch('integrations/get');
  window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
  scheduleDiagnosticsPolling();
};

const resetForm = () => {
  editingHookId.value = null;
  storeName.value = '';
  storeUrl.value = '';
  consumerKey.value = '';
  consumerSecret.value = '';
  requestStoreUrl.value = '';
  disableSslVerification.value = false;
  abandonedCartsEndpoint.value = '';
  formError.value = '';
};

const openCreateForm = () => {
  resetForm();
  viewMode.value = 'form';
};

const openEditForm = hook => {
  editingHookId.value = hook.id;
  storeName.value = hook.storeName || '';
  storeUrl.value = hook.storeUrl || '';
  requestStoreUrl.value =
    hook.requestStoreUrl && hook.requestStoreUrl !== hook.storeUrl
      ? hook.requestStoreUrl
      : '';
  disableSslVerification.value = Boolean(hook.disableSslVerification);
  abandonedCartsEndpoint.value = hook.abandonedCartsEndpoint || '';
  consumerKey.value = hook.consumerKey || '';
  consumerSecret.value = hook.consumerSecret || '';
  formError.value = '';
  viewMode.value = 'form';
};

const openOverview = () => {
  resetForm();
  viewMode.value = 'overview';
};

const handleSave = async () => {
  try {
    formError.value = '';
    isSubmitting.value = true;
    await authorizeWooCommerce(accountId.value, {
      hook_id: editingHookId.value,
      store_name: storeName.value,
      store_url: storeUrl.value,
      request_store_url: requestStoreUrl.value,
      consumer_key: consumerKey.value,
      consumer_secret: consumerSecret.value,
      disable_ssl_verification: disableSslVerification.value,
      conversation_panel_mode: conversationPanelMode.value,
      abandoned_carts_endpoint: abandonedCartsEndpoint.value,
    });
    await refreshIntegrationState();
    openOverview();
  } catch (err) {
    formError.value =
      err?.response?.data?.error || t('INTEGRATION_SETTINGS.WOOCOMMERCE.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const handleCopy = async value => {
  if (!value) {
    return;
  }

  await copyTextToClipboard(value);
  useAlert(t('COMPONENTS.CODE.COPY_SUCCESSFUL'));
};

const handleDeleteHook = async hookId => {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: integrationId.value,
      hookId,
    });
    await refreshIntegrationState();
    if (editingHookId.value === hookId) {
      openOverview();
    }
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE'));
  }
};

const handleRunDiagnostics = async hook => {
  try {
    diagnosticsRefreshHookId.value = hook.id;
    await authorizeWooCommerce(accountId.value, {
      hook_id: hook.id,
      store_name: hook.storeName,
      store_url: hook.storeUrl,
      request_store_url: hook.requestStoreUrl,
      consumer_key: hook.consumerKey,
      consumer_secret: hook.consumerSecret,
      disable_ssl_verification: hook.disableSslVerification,
      conversation_panel_mode: currentConversationPanelMode.value,
      abandoned_carts_endpoint: hook.abandonedCartsEndpoint,
    });
    await refreshIntegrationState();
    useAlert(t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.REFRESH_SUCCESS'));
  } catch (err) {
    useAlert(
      err?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.REFRESH_ERROR')
    );
  } finally {
    diagnosticsRefreshHookId.value = null;
  }
};

const handleSavePreferences = async () => {
  try {
    isSavingPreferences.value = true;
    await saveWooCommercePreferences(accountId.value, {
      conversation_panel_mode: conversationPanelMode.value,
    });
    await refreshIntegrationState();
    useAlert(t('INTEGRATION_SETTINGS.WOOCOMMERCE.GENERAL_SETTINGS_SAVED'));
  } catch (err) {
    useAlert(
      err?.response?.data?.error || t('INTEGRATION_SETTINGS.WOOCOMMERCE.ERROR')
    );
  } finally {
    isSavingPreferences.value = false;
  }
};

const initializeIntegration = async () => {
  await refreshIntegrationState();
  conversationPanelMode.value = currentConversationPanelMode.value;
  integrationLoaded.value = true;
};

onMounted(() => {
  initializeIntegration();
});

onUnmounted(() => {
  stopDiagnosticsPolling();
});

watch(
  connectedHooks,
  () => {
    conversationPanelMode.value = currentConversationPanelMode.value;
    scheduleDiagnosticsPolling();
  },
  { deep: true }
);
</script>

<template>
  <div
    data-target="woocommerce-integration-page"
    class="flex flex-col flex-1 gap-6 overflow-auto pt-1 pb-10"
  >
    <template v-if="integrationLoaded">
      <section
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div
          class="flex flex-col gap-4 p-6 lg:flex-row lg:items-center lg:justify-between"
        >
          <div class="max-w-3xl">
            <h2 class="mb-2 text-xl font-medium text-n-slate-12">
              {{ integrationName }}
            </h2>
            <p class="m-0 text-sm leading-6 text-n-slate-11">
              {{ integrationDescription }}
            </p>
          </div>
          <div class="flex gap-3">
            <Button
              v-if="viewMode === 'form'"
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="openOverview"
            />
            <Button
              teal
              data-target="woocommerce-open-connection-form"
              :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
              @click="openCreateForm"
            />
          </div>
        </div>
      </section>

      <section
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="border-b border-n-weak p-6">
          <h3 class="text-xl font-medium text-n-slate-12">
            {{
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.CONVERSATION_PANEL_MODE'
              )
            }}
          </h3>
          <p class="mb-0 mt-2 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.CONVERSATION_PANEL_MODE_DESCRIPTION'
              )
            }}
          </p>
        </div>

        <div class="p-6">
          <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <button
              v-for="option in conversationModeOptions"
              :key="option.value"
              type="button"
              class="rounded-lg border px-4 py-4 text-left transition-colors"
              :class="
                conversationPanelMode === option.value
                  ? 'border-n-brand bg-n-brand/10 text-n-slate-12'
                  : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:border-n-slate-6'
              "
              @click="conversationPanelMode = option.value"
            >
              <p class="mb-1 text-sm font-medium">
                {{ option.label }}
              </p>
              <p class="mb-0 text-xs leading-5 text-n-slate-10">
                {{ option.hint }}
              </p>
            </button>
          </div>

          <div class="mt-4 flex justify-end">
            <Button
              teal
              :disabled="!connectedHooks.length"
              :is-loading="isSavingPreferences"
              :label="savePreferencesLabel"
              @click="handleSavePreferences"
            />
          </div>
        </div>
      </section>

      <section
        v-if="viewMode === 'overview' && connectedHooks.length"
        data-target="woocommerce-connections-overview"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="grid grid-cols-1 gap-4 p-6 xl:grid-cols-2">
          <div
            v-for="hook in connectedHooks"
            :key="hook.id"
            class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
          >
            <div class="flex items-start justify-between gap-4">
              <div class="min-w-0 flex-1">
                <p class="mb-1 break-all text-sm font-medium text-n-slate-12">
                  {{ hook.storeName || hook.storeUrl }}
                </p>
                <p
                  v-if="hook.storeName"
                  class="mb-1 break-all text-xs text-n-slate-10"
                >
                  {{ hook.storeUrl }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  <span>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.ABANDONED_CARTS_ENDPOINT'
                      )
                    }}
                  </span>
                  <span class="mt-1 block break-all">
                    {{ hook.abandonedCartsEndpoint || '—' }}
                  </span>
                </p>
                <p
                  v-if="
                    hook.requestStoreUrl &&
                    hook.requestStoreUrl !== hook.storeUrl
                  "
                  class="mb-0 mt-3 text-xs text-n-slate-10"
                >
                  <span>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.REQUEST_STORE_URL'
                      )
                    }}
                  </span>
                  <span class="mt-1 block break-all">
                    {{ hook.requestStoreUrl }}
                  </span>
                </p>
                <p
                  v-if="hook.disableSslVerification"
                  class="mb-0 mt-3 text-xs text-n-amber-11"
                >
                  {{
                    $t('INTEGRATION_SETTINGS.WOOCOMMERCE.SSL_BYPASS_ENABLED')
                  }}
                </p>
              </div>
              <div class="flex gap-2">
                <Button
                  slate
                  xs
                  faded
                  :label="$t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')"
                  @click="openEditForm(hook)"
                />
                <Button
                  ruby
                  xs
                  faded
                  :label="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')"
                  @click="handleDeleteHook(hook.id)"
                />
              </div>
            </div>

            <div class="mt-4 space-y-4">
              <div>
                <div class="flex items-start justify-between gap-4">
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.WEBHOOK_URL') }}
                  </p>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="handleCopy(automationWebhookUrl)"
                  />
                </div>
                <code
                  class="mt-3 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
                >
                  {{ automationWebhookUrl }}
                </code>
              </div>

              <div>
                <div class="flex items-start justify-between gap-4">
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.AUTH_HEADER') }}
                  </p>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="
                      handleCopy(buildAutomationHeaderValue(hook.webhookToken))
                    "
                  />
                </div>
                <code
                  class="mt-3 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
                >
                  {{ buildAutomationHeaderValue(hook.webhookToken) }}
                </code>
                <p class="mb-0 mt-2 break-all text-xs text-n-slate-10">
                  {{ buildFallbackHeaderValue(hook.webhookToken) }}
                </p>
              </div>

              <div class="rounded-lg border border-n-weak bg-n-alpha-2 p-4">
                <div class="flex items-start justify-between gap-4">
                  <div>
                    <p class="mb-1 text-sm font-medium text-n-slate-12">
                      {{
                        $t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.TITLE')
                      }}
                    </p>
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{
                        $t(
                          'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.DESCRIPTION'
                        )
                      }}
                    </p>
                  </div>
                  <Button
                    slate
                    xs
                    faded
                    :label="
                      $t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.REFRESH')
                    "
                    :is-loading="diagnosticsRefreshHookId === hook.id"
                    @click="handleRunDiagnostics(hook)"
                  />
                </div>

                <div class="mt-4 space-y-3">
                  <div
                    v-for="diagnostic in buildDiagnostics(hook)"
                    :key="diagnostic.key"
                    class="flex items-start gap-3 rounded-md border border-n-weak bg-n-alpha-black2 px-3 py-3"
                  >
                    <span
                      class="inline-flex min-h-7 min-w-7 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
                      :class="statusClassMap[diagnostic.status]"
                    >
                      {{ statusGlyphMap[diagnostic.status] }}
                    </span>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <p class="mb-0 text-sm font-medium text-n-slate-12">
                          {{ diagnostic.label }}
                        </p>
                        <span class="text-xs text-n-slate-10">
                          {{ diagnosticStatusLabel(diagnostic.status) }}
                        </span>
                      </div>
                      <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-10">
                        {{ diagnostic.message }}
                      </p>
                      <p
                        v-if="diagnostic.meta"
                        class="mb-0 mt-1 text-[11px] text-n-slate-10"
                      >
                        {{ diagnostic.meta }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="mt-4">
                  <p class="mb-2 text-sm font-medium text-n-slate-12">
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.LOG_TITLE'
                      )
                    }}
                  </p>
                  <div v-if="hook.diagnosticsLog.length" class="space-y-2">
                    <div
                      v-for="(entry, index) in hook.diagnosticsLog.slice(0, 6)"
                      :key="`${hook.id}-${index}-${entry.at}`"
                      class="rounded-md border border-n-weak bg-n-alpha-black2 px-3 py-3"
                    >
                      <div
                        class="flex items-center gap-2 text-[11px] text-n-slate-10"
                      >
                        <span class="font-medium uppercase">{{
                          entry.level
                        }}</span>
                        <span>{{ entry.scope }}</span>
                        <span v-if="formatTimestamp(entry.at)">
                          {{ formatTimestamp(entry.at) }}
                        </span>
                      </div>
                      <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
                        {{ entry.message }}
                      </p>
                    </div>
                  </div>
                  <p v-else class="mb-0 text-xs text-n-slate-10">
                    {{
                      $t('INTEGRATION_SETTINGS.WOOCOMMERCE.DIAGNOSTICS.NO_LOGS')
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        v-if="viewMode === 'overview' && !connectedHooks.length"
        class="rounded-xl bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
      >
        <p class="m-0 text-sm leading-6 text-n-slate-11">
          {{ $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', { integrationId }) }}
        </p>
      </section>

      <section
        v-if="viewMode === 'form'"
        data-target="woocommerce-connection-form"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="border-b border-n-weak p-6">
          <h3 class="text-xl font-medium text-n-slate-12">
            {{
              selectedHook
                ? $t('INTEGRATION_SETTINGS.WEBHOOK.CONFIGURE')
                : $t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')
            }}
          </h3>
          <p class="mb-0 mt-2 max-w-4xl text-sm leading-6 text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.CONNECTION_HELP') }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4 p-6 lg:grid-cols-2">
          <Input
            v-model="storeName"
            data-target="woocommerce-store-name"
            :label="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.STORE_NAME')"
          />
          <Input
            v-model="storeUrl"
            data-target="woocommerce-store-url"
            type="url"
            :label="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.STORE_URL')"
          />
          <Input
            v-model="consumerKey"
            data-target="woocommerce-consumer-key"
            :label="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.CONSUMER_KEY')"
          />
          <Input
            v-model="consumerSecret"
            data-target="woocommerce-consumer-secret"
            type="password"
            autocomplete="new-password"
            :label="
              $t('INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.CONSUMER_SECRET')
            "
          />
          <Input
            v-model="requestStoreUrl"
            data-target="woocommerce-request-store-url"
            type="url"
            :label="
              $t('INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.REQUEST_STORE_URL')
            "
            :placeholder="storeUrl || undefined"
          />
          <Input
            v-model="abandonedCartsEndpoint"
            data-target="woocommerce-abandoned-carts-endpoint"
            :label="
              $t(
                'INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.ABANDONED_CARTS_ENDPOINT'
              )
            "
          />
          <div class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4">
            <div class="flex items-center gap-3">
              <Switch v-model="disableSslVerification" />
              <div>
                <p class="mb-1 text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.WOOCOMMERCE.FIELDS.DISABLE_SSL_VERIFICATION'
                    )
                  }}
                </p>
                <p class="mb-0 text-xs leading-5 text-n-slate-10">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.WOOCOMMERCE.DISABLE_SSL_VERIFICATION_HINT'
                    )
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 pb-6 -mt-2">
          <p class="mb-0 text-xs leading-5 text-n-slate-10">
            {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.REQUEST_STORE_URL_HINT') }}
          </p>
        </div>

        <div class="border-t border-n-weak p-6">
          <div class="flex gap-3">
            <Button
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="openOverview"
            />
            <Button
              teal
              data-target="woocommerce-save-connection"
              :label="submitLabel"
              :is-loading="isSubmitting"
              @click="handleSave"
            />
          </div>
        </div>
        <p v-if="formError" class="m-0 px-6 pb-6 text-sm text-n-ruby-9">
          {{ formError }}
        </p>
      </section>

      <div
        v-if="error"
        class="flex flex-1 items-center justify-center rounded-md bg-n-alpha-3 p-6 shadow outline outline-1 outline-n-container"
      >
        <p class="text-n-ruby-9">
          {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.ERROR') }}
        </p>
      </div>

      <section
        data-target="woocommerce-automation-reference"
        class="rounded-xl bg-n-alpha-3 shadow outline outline-1 outline-n-container"
      >
        <div class="p-6">
          <AccordionItem
            :title="$t('INTEGRATION_SETTINGS.WOOCOMMERCE.AUTOMATION_TITLE')"
            :is-open="isAutomationAccordionOpen"
            @toggle="isAutomationAccordionOpen = !isAutomationAccordionOpen"
          >
            <p class="mb-0 px-2 pb-4 text-sm leading-6 text-n-slate-11">
              {{
                $t('INTEGRATION_SETTINGS.WOOCOMMERCE.AUTOMATION_DESCRIPTION')
              }}
            </p>

            <div class="grid grid-cols-1 gap-4 px-2 pb-4 lg:grid-cols-2">
              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <p class="mb-1 text-sm font-medium text-n-slate-12">
                      {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.WEBHOOK_URL') }}
                    </p>
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{
                        $t('INTEGRATION_SETTINGS.WOOCOMMERCE.STORE_URL_HINT', {
                          storeUrl: selectedHook?.storeUrl || storeUrl || '—',
                        })
                      }}
                    </p>
                  </div>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="handleCopy(automationWebhookUrl)"
                  />
                </div>
                <code
                  class="mt-4 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
                >
                  {{ automationWebhookUrl }}
                </code>
              </div>

              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <p class="mb-1 text-sm font-medium text-n-slate-12">
                      {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.AUTH_HEADER') }}
                    </p>
                  </div>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="
                      handleCopy(
                        buildAutomationHeaderValue(
                          selectedHook?.webhookToken || '...'
                        )
                      )
                    "
                  />
                </div>
                <code
                  class="mt-4 block break-all rounded-md bg-n-alpha-2 px-3 py-3 text-xs text-n-slate-11"
                >
                  {{
                    buildAutomationHeaderValue(
                      selectedHook?.webhookToken || '...'
                    )
                  }}
                </code>
                <p class="mb-0 mt-3 break-all text-xs text-n-slate-10">
                  {{
                    buildFallbackHeaderValue(
                      selectedHook?.webhookToken || '...'
                    )
                  }}
                </p>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-4 px-2 pb-4 lg:grid-cols-2">
              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <p class="mb-3 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.EXPECTED_EVENTS') }}
                </p>
                <ul class="mb-0 space-y-2 pl-4 text-sm text-n-slate-11">
                  <li v-for="eventKey in automationEventKeys" :key="eventKey">
                    <code>{{ eventKey }}</code>
                    <span class="ml-2">{{
                      automationEventLabels[eventKey] || eventKey
                    }}</span>
                  </li>
                </ul>
              </div>

              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <p class="mb-3 text-sm font-medium text-n-slate-12">
                  {{ $t('INTEGRATION_SETTINGS.WOOCOMMERCE.EXPECTED_PAYLOAD') }}
                </p>
                <ul class="mb-0 space-y-2 pl-4 text-sm text-n-slate-11">
                  <li v-for="field in payloadFields" :key="field">
                    <code>{{ field }}</code>
                  </li>
                </ul>
              </div>
            </div>

            <div class="grid grid-cols-1 gap-4 px-2 pb-4 xl:grid-cols-2">
              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <p class="mb-1 text-sm font-medium text-n-slate-12">
                      {{
                        $t(
                          'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_PRIMARY_TITLE'
                        )
                      }}
                    </p>
                    <p class="mb-0 text-xs leading-5 text-n-slate-10">
                      {{
                        $t(
                          'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_DESCRIPTION'
                        )
                      }}
                    </p>
                  </div>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="handleCopy(abandonedCartExample)"
                  />
                </div>
                <pre
                  class="mt-4 overflow-x-auto rounded-md bg-n-alpha-2 px-3 py-3 text-xs leading-6 text-n-slate-11"
                ><code>{{ abandonedCartExample }}</code></pre>
              </div>

              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <p class="mb-1 text-sm font-medium text-n-slate-12">
                      {{
                        $t(
                          'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_FALLBACK_TITLE'
                        )
                      }}
                    </p>
                    <p class="mb-0 text-xs leading-5 text-n-slate-10">
                      {{
                        $t('INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_HINT')
                      }}
                    </p>
                  </div>
                  <Button
                    slate
                    xs
                    faded
                    :label="$t('COMPONENTS.CODE.BUTTON_TEXT')"
                    @click="handleCopy(abandonedCartFallbackExample)"
                  />
                </div>
                <pre
                  class="mt-4 overflow-x-auto rounded-md bg-n-alpha-2 px-3 py-3 text-xs leading-6 text-n-slate-11"
                ><code>{{ abandonedCartFallbackExample }}</code></pre>
              </div>
            </div>

            <div class="px-2 pb-2">
              <div
                class="rounded-lg border border-n-weak bg-n-alpha-black2 p-4"
              >
                <p class="mb-3 text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_RULES'
                    )
                  }}
                </p>
                <ul class="mb-0 space-y-2 pl-4 text-sm text-n-slate-11">
                  <li>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_RULE_ITEMS.RESPONSE_ARRAY'
                      )
                    }}
                  </li>
                  <li>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_RULE_ITEMS.CUSTOMER_BLOCK'
                      )
                    }}
                  </li>
                  <li>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_RULE_ITEMS.CONTACT_IDENTIFIER'
                      )
                    }}
                  </li>
                  <li>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_RULE_ITEMS.CHECKOUT_URL'
                      )
                    }}
                  </li>
                  <li>
                    {{
                      $t(
                        'INTEGRATION_SETTINGS.WOOCOMMERCE.ABANDONED_PAYLOAD_RULE_ITEMS.TOTAL_CURRENCY'
                      )
                    }}
                  </li>
                </ul>
              </div>
            </div>
          </AccordionItem>
        </div>
      </section>
    </template>
  </div>
</template>
